//@flow
import * as React from 'react';
import {
    Columns,
    Stack,
    Column,
    Text,
    Box,
    Button,
    Position
} from '@locus-taxy/neo-ui';
import { SelectTypes, Select } from 'common/components/core/select';
import { EnhancedField } from 'common/components/core/form/Field';
import { clientIdSelector } from 'common/redux/selectors/sessionSelector';
import * as Immutable from 'immutable';
import _ from 'lodash';
import { FormFieldSwitch } from 'common/components/core/form/FormFieldSwitch';
import {
    PjpInsightsDashboard,
    PjpInsightsDashboardData
} from 'common/models/internal/PjpInsightsDashboard';
import PjpMetricPreview from 'common/entity/pjpMaster/PjpMetricPreview';
import { connect } from 'react-redux';
import PjpInsightsActions from 'common/redux/actions/pjpInsights';
import { getAllPjpInsightsDashboards } from 'common/redux/selectors/pjpInsightsSelectors';

import { PjpMetricGraph } from 'common/models/internal/PjpMetricGraph';

import type { List } from 'immutable';
import type { PjpInsightsMetadata } from 'common/models/internal/PjpInsightsMetadata';

import enhancedFieldFactory from 'common/components/core/form/EnhancedFieldFactory';
import { type TFunction } from 'react-i18next';
import { type ThemeType } from 'react-css-themr';

const EnhancedSelect = enhancedFieldFactory({
    FieldComponent: Select,
    attachPopover: true
});

const REF_LINE = {
    type: 'ABSOLUTE',
    value: 0,
    label: null,
    alwaysShow: false
};

const DEFAULT_METRIC_DATA = PjpMetricGraph.fromJS({
    id: '',
    description: '',
    graphQueries: [0],
    includeExistingBeat: false,
    statisticQueries: [0],
    isSingleDayGraph: true,
    refLines: [REF_LINE],
    queries: [
        {
            metric: null,
            groupBy: [],
            aggregationType: null
        }
    ]
});

const DEFAULT_PJP_INSIGHTS_DASHBOARD = PjpInsightsDashboard.fromJS({
    clientId: '',
    dashboardId: '',
    dashboardData: null,
    parentDashboardId: '',
    label: '',
    isCustom: true,
    queryValidations: null,
    sequence: 0,
    active: true,
    dashboardType: 'PJP',
    workflowType: null
});

type Props = {
    metricsMetadata: List<PjpInsightsMetadata>,
    clearConfiguredMetric: () => void,
    editingMetric: ?PjpMetricGraph,
    t: TFunction,
    fetchPjpChartsData: () => void,
    planId: string,
    allDashboards: Array<PjpInsightsDashboard>,
    fetchPjpMetricData: (
        metricId: string,
        aggregate: string,
        groupBy: Array<string>,
        graphId: string,
        includeExistingBeat: boolean,
        isEditMetricActive: boolean,
        flushExisting: boolean
    ) => void,
    addPjpInsightsDashboard: (
        clientId: string,
        pjpInsightsDashboard: PjpInsightsDashboard
    ) => void,
    theme: ThemeType,
    onBackClick: () => void,
    clientId: string,
    planId: string,
    isMetricLoading: boolean
};

type State = {
    includeExistingBeat: boolean,
    graphId: ?string,
    graphDescription: ?string,
    pjpInsightsDashboard: PjpInsightsDashboard,
    selectedGroupBy: Array<{ value: string, label: string }>,
    selectedAggregate: ?string,
    selectedMetric: ?{
        value: string,
        label: string
    },
    graph: PjpMetricGraph,
    graphIdError: ?string,
    aggError: ?string,
    groupByError: ?string,
    metricError: ?string,
    isConfigValid: boolean
};

class EditPjpMetricContainer extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        const editingMetric = props.editingMetric?.queries.get(0).metric;
        const selectedMetric = editingMetric
            ? {
                  value: editingMetric,
                  label: props.t(`pjpAnalytics.${editingMetric}`)
              }
            : null;
        const selectedGroupBy = props.editingMetric?.queries.get(0).groupBy;
        const updatedGroupBys = selectedGroupBy
            ? selectedGroupBy
                  .map(group => {
                      return {
                          value: group,
                          label: props.t(
                              `pjpAnalytics.groupBys.${group.toLowerCase()}`
                          )
                      };
                  })
                  .toJS()
            : [];
        this.state = {
            includeExistingBeat:
                props.editingMetric?.includeExistingBeat || false,
            graphId: props.editingMetric?.id || null,
            graphDescription: props.editingMetric?.description || null,
            metricsMetadata: props.metricsMetadata,
            selectedMetric,
            selectedAggregate:
                props.editingMetric?.queries.get(0).aggregationType || null,
            selectedGroupBy: updatedGroupBys,
            graph: props.editingMetric || DEFAULT_METRIC_DATA,
            pjpInsightsDashboard: DEFAULT_PJP_INSIGHTS_DASHBOARD,
            graphIdError: null,
            aggError: null,
            groupByError: null,
            metricError: null,
            isConfigValid: props.editingMetric ? true : false
        };
    }

    componentDidMount() {
        const {
            selectedGroupBy,
            selectedAggregate,
            selectedMetric
        } = this.state;
        if (
            selectedGroupBy.length !== 0 &&
            selectedAggregate &&
            selectedMetric
        ) {
            this.fetchPjpMetric();
        }
    }

    componentDidUpdate(nextProps: Props, nextState: State) {
        const {
            selectedGroupBy,
            selectedAggregate,
            selectedMetric,
            includeExistingBeat
        } = nextState;

        const {
            selectedGroupBy: prevSelectedGroupBy,
            selectedAggregate: prevSelectedAggregate,
            selectedMetric: prevSelectedMetric,
            includeExistingBeat: prevIncludeExistingBeat
        } = this.state;
        const isConfigValid = this.isMetricConfigurationValid();
        if (
            isConfigValid &&
            (selectedAggregate !== prevSelectedAggregate ||
                selectedMetric?.value !== prevSelectedMetric?.value ||
                !_.isEqual(selectedGroupBy, prevSelectedGroupBy) ||
                prevIncludeExistingBeat !== includeExistingBeat)
        ) {
            this.fetchPjpMetric();
        }
    }

    fetchPjpMetric = () => {
        const { fetchPjpMetricData } = this.props;
        const updatedGraph = this.state.graph.toJS();
        const { includeExistingBeat } = this.state;
        const { id: graphId } = updatedGraph;
        const [metricQueryParams] = updatedGraph.queries;
        const { metric, groupBy, aggregationType } = metricQueryParams;
        if (metric && groupBy.length !== 0 && aggregationType) {
            fetchPjpMetricData &&
                fetchPjpMetricData(
                    metric,
                    aggregationType,
                    groupBy,
                    graphId,
                    includeExistingBeat,
                    true,
                    true
                );
        }
    };

    getAllMetricOptions = () => {
        const { metricsMetadata } = this.props;
        const allMetricOptions = [];
        metricsMetadata.forEach(metric => {
            allMetricOptions.push({
                value: metric.get('metric'),
                label: metric.get('label')
            });
        });
        return allMetricOptions;
    };

    getMetricAggregationOptions = () => {
        const { t, metricsMetadata } = this.props;
        const { selectedMetric } = this.state;
        if (!selectedMetric) {
            return [];
        }
        const selectedMetricData = metricsMetadata.find(
            metric => selectedMetric.value === metric.metric
        );
        const aggregate = selectedMetricData.aggregation.map(aggregate => {
            return {
                value: aggregate,
                label: t(
                    `pjpAnalytics.aggregationType.${aggregate.toUpperCase()}`
                )
            };
        });
        return aggregate;
    };

    getMetricGroupBy = () => {
        const { t, metricsMetadata } = this.props;
        const { selectedMetric } = this.state;
        if (!selectedMetric) {
            return [];
        }
        const selectedMetricData = metricsMetadata.find(
            metric => metric.metric === selectedMetric.value
        );
        const groupBy = selectedMetricData.groupBy.map(group => {
            return {
                value: group.groupBy,
                label: t(group.label)
            };
        });
        return groupBy;
    };

    onMetricChange = (selectedMetric: { value: string, label: string }) => {
        const { clearConfiguredMetric } = this.props;
        let [updatedQuery] = this.state.graph.queries;
        updatedQuery = updatedQuery
            .setMetric(selectedMetric.value)
            .setAggregationType(null)
            .setGroupBy(Immutable.List([]));
        clearConfiguredMetric();
        this.setState(prevState => {
            const updatedMetric = prevState.graph.setQueries(
                Immutable.List([updatedQuery])
            );
            const hasError = updatedQuery.validateMetric();
            return {
                graph: updatedMetric,
                selectedMetric,
                selectedAggregate: null,
                selectedGroupBy: [],
                metricError: hasError ? hasError : null
            };
        });
    };

    onAggregateChange = (selectedAggregate: {
        value: string,
        label: string
    }) => {
        const { clearConfiguredMetric } = this.props;
        if (selectedAggregate) {
            let [updatedQuery] = this.state.graph.queries;
            updatedQuery = updatedQuery
                .setAggregationType(selectedAggregate.value)
                .setGroupBy([]);

            clearConfiguredMetric();
            this.setState(prevState => {
                const updatedMetric = prevState.graph.setQueries(
                    Immutable.List([updatedQuery])
                );
                const hasError = updatedQuery.validateAggregationType();
                return {
                    graph: updatedMetric,
                    selectedAggregate: selectedAggregate.value,
                    selectedGroupBy: [],
                    aggError: hasError ? hasError : null
                };
            });
        }
    };

    onGroupByChange = (
        selectedGroupBy: Array<{ value: string, label: string }>
    ) => {
        const { clearConfiguredMetric } = this.props;
        if (selectedGroupBy) {
            let [updatedQuery] = this.state.graph.queries;
            const updatedGroupByValues = selectedGroupBy.map(
                groupBy => groupBy.value
            );
            updatedQuery = updatedQuery.setGroupBy(
                Immutable.List([...updatedGroupByValues])
            );
            clearConfiguredMetric();
            this.setState(prevState => {
                const updatedMetric = prevState.graph.setQueries(
                    Immutable.List([updatedQuery])
                );
                const hasError = updatedQuery.validateGroupBy();
                return {
                    graph: updatedMetric,
                    selectedGroupBy,
                    groupByError: hasError ? hasError : null
                };
            });
        }
    };

    onGraphIdChange = (graphId: string) => {
        const { graph } = this.state;
        const updatedMetric = graph.setId(graphId);
        const hasError = updatedMetric.getValidationMessage('id');
        this.setState({
            graph: updatedMetric,
            graphId,
            graphIdError: hasError || null
        });
    };

    onTitleChange = (graphDescription: string) => {
        const { graph } = this.state;
        this.setState({
            graph: graph.setDescription(graphDescription)
        });
    };

    aggregateHasError = () => {
        const { graph } = this.state;
        const aggregateError = graph.queries.get(0).validateAggregationType();
        return aggregateError;
    };

    groupByHasError = () => {
        const { graph } = this.state;
        const groupByError = graph.queries.get(0).validateGroupBy();
        return groupByError;
    };

    metricHasError = () => {
        const { queries } = this.state.graph;
        const selectedQuery = queries.get(0);
        const metricError = selectedQuery.validateMetric();
        return metricError;
    };

    isMetricConfigurationValid = () => {
        const { graph } = this.state;
        const hasError =
            graph.hasValidationErrors() ||
            this.metricHasError() ||
            this.aggregateHasError() ||
            this.groupByHasError();
        return !hasError;
    };

    onSaveClick = () => {
        const { graph, pjpInsightsDashboard } = this.state;
        let updatedPjpInsightsDashboard = pjpInsightsDashboard;
        const {
            clientId,
            planId,
            addPjpInsightsDashboard,
            allDashboards,
            fetchPjpChartsData,
            clearConfiguredMetric
        } = this.props;

        if (!this.isMetricConfigurationValid()) {
            this.setState({
                metricError: this.metricHasError(),
                groupByError: this.groupByHasError(),
                aggError: this.aggregateHasError(),
                graphIdError: graph.validateId()
            });
        } else {
            const dashboardData = [graph.toJS()];
            const pjpDashboardData = PjpInsightsDashboardData.fromJS({
                graphs: dashboardData
            });

            updatedPjpInsightsDashboard = updatedPjpInsightsDashboard
                .setClientId(clientId)
                .setLabel(graph.id)
                .setDashboardId(graph.id)
                .setDashboardData(pjpDashboardData)
                .setSequence(allDashboards.length + 1)
                .setLabel(graph.id)
                .setParentDashboardId(planId);
            addPjpInsightsDashboard(clientId, updatedPjpInsightsDashboard).then(
                () => {
                    this.onBackClick();
                    fetchPjpChartsData && fetchPjpChartsData();
                    clearConfiguredMetric && clearConfiguredMetric();
                }
            );
        }
    };

    onBackClick = () => {
        const { onBackClick } = this.props;
        onBackClick();
    };

    clearConfiguredMetric = () => {
        const { clearConfiguredMetric } = this.props;
        clearConfiguredMetric && clearConfiguredMetric();
    };

    toggleExistingBeat = () => {
        const { includeExistingBeat, graph } = this.state;
        this.setState({
            graph: graph.setIncludeExistingBeat(!includeExistingBeat),
            includeExistingBeat: !includeExistingBeat
        });
    };

    renderPreviewEmptyState = () => {
        const { t } = this.props;
        return (
            <Box backgroundColor="white" height="fluid" p={4}>
                <Columns alignY="center" align="center" height="fluid">
                    <Stack space="large" height="auto" align="center">
                        <Text fontSize={3} fontWeight="600">
                            {t('pjpAnalytics.metricGraphPreview')}
                        </Text>
                        <Text fontSize={1} fontWeight="600">
                            {t('pjpAnalytics.emptyStateEditChartInfo')}
                        </Text>
                    </Stack>
                </Columns>
            </Box>
        );
    };

    renderPreview(metricConfigValid: boolean) {
        const { planId, t } = this.props;
        const { graph } = this.state;

        return (
            <Column width="fluid">
                {metricConfigValid ? (
                    <PjpMetricPreview
                        isEdit
                        t={t}
                        planId={planId}
                        graph={graph}
                    />
                ) : (
                    this.renderPreviewEmptyState()
                )}
            </Column>
        );
    }
    render() {
        const { includeExistingBeat, metricError } = this.state;
        const { theme, editingMetric, t } = this.props;
        const isMetricConfigValid = this.isMetricConfigurationValid();
        return (
            <Box backgroundColor="grey.100" height="560px" p={2}>
                <Stack height="fluid">
                    <Position
                        p={4}
                        position="fixed"
                        width="83vw"
                        backgroundColor="grey.100"
                        sx={{ zIndex: 1 }}
                    >
                        <Columns space="medium" alignY="center">
                            <Column width="content">
                                <Button
                                    appearance="icon"
                                    icon="arrow_chevron_left"
                                    size="large"
                                    onClick={this.onBackClick}
                                />
                            </Column>
                            <Column width="fluid">
                                <Text fontSize={3} fontWeight="600">
                                    {editingMetric
                                        ? t('pjpAnalytics.edit')
                                        : t('pjpAnalytics.add')}{' '}
                                    {t('pjpAnalytics.metricGraph')}
                                </Text>
                            </Column>
                            <Columns space="medium">
                                <Button
                                    appearance="ghost"
                                    onClick={this.onBackClick}
                                >
                                    {t('translation:dismiss')}
                                </Button>

                                <Button
                                    appearance="solid"
                                    palette="primary"
                                    onClick={this.onSaveClick}
                                >
                                    {t('translation:save')}
                                </Button>
                            </Columns>
                        </Columns>
                    </Position>
                    <Box height="fluid" p={4} sx={{ marginTop: '8rem' }}>
                        <Columns height="fluid" space="small">
                            <Column width="1/4">
                                <Stack space="large" height="fluid">
                                    <Stack space="small">
                                        <Text fontWeight="600" fontSize={1}>
                                            {t('pjpAnalytics.graphId')}{' '}
                                            <sup>*</sup>
                                        </Text>

                                        <EnhancedField
                                            place="right"
                                            error={this.state.graphIdError}
                                            hasError={this.state.graphIdError}
                                            onChange={this.onGraphIdChange}
                                            value={this.state.graph.id || ''}
                                            element={'input'}
                                            theme={theme}
                                            placeholder={t(
                                                'pjpAnalytics.idPlaceholder'
                                            )}
                                            className={theme.existingBeatToggle}
                                        />
                                    </Stack>
                                    <Stack space="small">
                                        <Text fontWeight="600" fontSize={1}>
                                            {t('pjpAnalytics.title')}
                                        </Text>

                                        <EnhancedField
                                            onChange={this.onTitleChange}
                                            value={
                                                this.state.graph.description ||
                                                ''
                                            }
                                            element={'input'}
                                            theme={theme}
                                            placeholder={t(
                                                'pjpAnalytics.titlePlaceholder'
                                            )}
                                        />
                                    </Stack>

                                    <Text fontSize={3} fontWeight="600">
                                        {t('pjpAnalytics.metricSelection')}
                                    </Text>

                                    <Stack space="small">
                                        <Text fontWeight="600" fontSize={1}>
                                            {t('pjpAnalytics.selectMetric')}
                                        </Text>

                                        <EnhancedSelect
                                            place="right"
                                            hasError={metricError}
                                            error={metricError}
                                            placeholder={t(
                                                'translation:metric'
                                            )}
                                            showPlaceholderIcon={false}
                                            theme={theme}
                                            isClearable={false}
                                            selectType={SelectTypes.Outline}
                                            options={this.getAllMetricOptions()}
                                            onChange={this.onMetricChange}
                                            label={t(
                                                'pjpAnalytics.selectMetric'
                                            )}
                                            value={this.state.selectedMetric}
                                        />
                                    </Stack>

                                    <Stack space="small">
                                        <Text fontWeight="600" fontSize={1}>
                                            {t(
                                                'pjpAnalytics.selectAggregation'
                                            )}
                                        </Text>

                                        <EnhancedSelect
                                            place="right"
                                            error={this.state.aggError}
                                            hasError={this.state.aggError}
                                            placeholder="Aggregation"
                                            showPlaceholderIcon={false}
                                            theme={theme}
                                            isClearable={false}
                                            selectType={SelectTypes.Outline}
                                            options={this.getMetricAggregationOptions()}
                                            label={t(
                                                'pjpAnalytics.selectAggregation'
                                            )}
                                            onChange={this.onAggregateChange}
                                            value={this.state.selectedAggregate}
                                        />
                                    </Stack>

                                    <Stack space="small">
                                        <Text fontWeight="600" fontSize={1}>
                                            {t('pjpAnalytics.selectGroupBy')}
                                        </Text>

                                        <EnhancedSelect
                                            place="right"
                                            error={this.state.groupByError}
                                            hasError={this.state.groupByError}
                                            placeholder="Group by"
                                            showPlaceholderIcon={false}
                                            isMulti
                                            theme={theme}
                                            isClearable={false}
                                            selectType={SelectTypes.Outline}
                                            onChange={this.onGroupByChange}
                                            options={this.getMetricGroupBy()}
                                            label={t(
                                                'pjpAnalytics.selectGroupBy'
                                            )}
                                            value={this.state.selectedGroupBy}
                                        />
                                    </Stack>
                                </Stack>
                            </Column>
                            <Column width="3/4">
                                <Stack height="fluid" space="small">
                                    <Columns align="right">
                                        <Box p={0.5} pr={4}>
                                            <FormFieldSwitch
                                                hideLabel
                                                theme={theme}
                                                checked={includeExistingBeat}
                                                onChange={
                                                    this.toggleExistingBeat
                                                }
                                            />
                                        </Box>

                                        <Text fontSize={2} fontWeight="600">
                                            {' '}
                                            {t(
                                                'pjpAnalytics.includeExistingBeat'
                                            )}
                                        </Text>
                                    </Columns>
                                    <Columns
                                        alignY="center"
                                        align="center"
                                        height="fluid"
                                    >
                                        {this.renderPreview(
                                            isMetricConfigValid
                                        )}
                                    </Columns>
                                </Stack>
                            </Column>
                        </Columns>
                    </Box>
                </Stack>
            </Box>
        );
    }
}
const mapStateToProps = (state, ownProps: Props) => {
    const clientId = clientIdSelector(state, ownProps);
    const allDashboards = getAllPjpInsightsDashboards(state, ownProps);
    return {
        ...ownProps,
        clientId,
        allDashboards
    };
};
const mapDispatchToProps = {
    addPjpInsightsDashboard: PjpInsightsActions.addPjpInsightsDashboard
};
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(EditPjpMetricContainer);
